/**
 * Referral Settings Tab
 *
 * Flat FormState pattern (mirrors SubscriptionSettingsTab): load reward-day
 * config on mount, edit three number inputs, Save (PUT) / Reset (re-fetch).
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Save, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { toast } from 'sonner';
import { useI18n } from '@/i18n';
import { referralApi, type ReferralConfig } from '@/lib/api/referral';

const MIN_DAYS = 0;
const MAX_DAYS = 3650;

interface FormState {
  referrer_days_on_signup: number;
  referrer_days_on_first_purchase: number;
  referee_days_on_signup: number;
}

function formStateFromConfig(data: ReferralConfig): FormState {
  return {
    referrer_days_on_signup: data.referrer_days_on_signup,
    referrer_days_on_first_purchase: data.referrer_days_on_first_purchase,
    referee_days_on_signup: data.referee_days_on_signup,
  };
}

function outOfRange(value: number): boolean {
  return !Number.isInteger(value) || value < MIN_DAYS || value > MAX_DAYS;
}

export function ReferralSettingsTab() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['referral-config'],
    queryFn: referralApi.getConfig,
  });

  const [formState, setFormState] = useState<FormState | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  if (data && !formState) {
    setFormState(formStateFromConfig(data));
  }

  const mutation = useMutation({
    mutationFn: referralApi.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-config'] });
      toast.success(t('referralPage.savedSuccess'));
      setHasChanges(false);
    },
    onError: (err: Error) => {
      toast.error(`${t('referralPage.saveFailed')}: ${err.message}`);
    },
  });

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    if (!formState) return;
    setFormState({ ...formState, [key]: value });
    setHasChanges(true);
  };

  const hasInvalidField =
    formState != null &&
    (outOfRange(formState.referrer_days_on_signup) ||
      outOfRange(formState.referrer_days_on_first_purchase) ||
      outOfRange(formState.referee_days_on_signup));

  const handleSave = () => {
    if (!formState || hasInvalidField) return;
    mutation.mutate({
      referrer_days_on_signup: formState.referrer_days_on_signup,
      referrer_days_on_first_purchase: formState.referrer_days_on_first_purchase,
      referee_days_on_signup: formState.referee_days_on_signup,
    });
  };

  const handleReset = () => {
    if (!data) return;
    setFormState(formStateFromConfig(data));
    setHasChanges(false);
  };

  const fields: { key: keyof FormState; label: string; help: string }[] = [
    {
      key: 'referrer_days_on_signup',
      label: t('referralPage.referrerSignupLabel'),
      help: t('referralPage.referrerSignupHelp'),
    },
    {
      key: 'referrer_days_on_first_purchase',
      label: t('referralPage.referrerPurchaseLabel'),
      help: t('referralPage.referrerPurchaseHelp'),
    },
    {
      key: 'referee_days_on_signup',
      label: t('referralPage.refereeSignupLabel'),
      help: t('referralPage.refereeSignupHelp'),
    },
  ];

  return (
    <div className="max-w-2xl">
      <div className="flex justify-end gap-2 mb-6">
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('referralPage.refresh')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || hasInvalidField || mutation.isPending}
        >
          <Save className="w-4 h-4 mr-2" />
          {mutation.isPending ? t('referralPage.saving') : t('referralPage.save')}
        </Button>
      </div>

      {error && (
        <Card className="mb-6 border-destructive">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
            <div>
              <h3 className="text-destructive font-semibold">{t('referralPage.errorLoading')}</h3>
              <p className="text-destructive text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </Card>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      )}

      {formState && (
        <Card>
          <h2 className="text-lg font-semibold text-foreground mb-1">
            {t('referralPage.sectionTitle')}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">{t('referralPage.sectionDesc')}</p>

          <div className="space-y-6">
            {fields.map((field) => {
              const value = formState[field.key];
              const invalid = outOfRange(value);
              return (
                <Input
                  key={field.key}
                  type="number"
                  min={MIN_DAYS}
                  max={MAX_DAYS}
                  label={field.label}
                  value={value}
                  onChange={(e) =>
                    handleChange(field.key, parseInt(e.target.value, 10) || 0)
                  }
                  error={invalid ? t('referralPage.rangeError') : undefined}
                  helperText={field.help}
                />
              );
            })}
          </div>

          <p className="mt-6 text-xs text-muted-foreground">{t('referralPage.rangeHint')}</p>

          {hasChanges && (
            <div className="flex justify-end mt-6">
              <Button variant="outline" onClick={handleReset}>
                {t('referralPage.reset')}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
